import { LOG_API_CALL_DURATION_ENABLE } from '@aibees/shared/constants';
import {
  FILTER_CHANGE_PERF_MARK,
  FILTER_CHANGE_TO_API_CALL_MEASURE,
} from '@aibees/shared/constants/app';
import { ErrorResponse } from '@aibees/shared/definition';
import { useDebounce } from '@aibees/shared/hooks';
import { useRateLimitReachedStore } from '@aibees/shared/store';
import { useLatestApiErrorStore } from '@aibees/shared/store/latest-api-error-store';
import { DURATION_MS as MS } from '@aibees/shared/utils';
import { QueryKey, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import baseFetcher from '../fetcher/base-fetcher';
import { toErrorResponse } from '../utils';

export function useBaseQuery<Req = unknown, Res = unknown, SelectedRes = Res>(
  key: QueryKey,
  config: AxiosRequestConfig<Req>,
  options?: UseQueryOptions<Res, ErrorResponse, SelectedRes> & {
    delay?: number;
    logTime?: boolean;
  }
) {
  const enabled = useDebounce(true, MS.seconds(options?.delay ?? 0), false);
  const { setIsError: setRateLimitReached } = useRateLimitReachedStore();
  const { setError } = useLatestApiErrorStore();

  return useQuery<Res, ErrorResponse, SelectedRes>(
    key,
    () => baseFetcher({ ...config }),
    {
      enabled, // TODO this must be listed after ...options
      ...options,
      onError: (error) => {
        if (error?.status === 429) {
          setRateLimitReached(true);
        }
        const err = toErrorResponse(error);
        setError(err);
      },
      behavior: {
        onFetch: () => {
          if (options?.logTime && LOG_API_CALL_DURATION_ENABLE) {
            const marks = performance.getEntriesByName(FILTER_CHANGE_PERF_MARK);
            if (marks.length > 0) {
              const apiStartMark = `${FILTER_CHANGE_PERF_MARK}-api-start`;
              performance.mark(apiStartMark);
              performance.measure(
                FILTER_CHANGE_TO_API_CALL_MEASURE,
                FILTER_CHANGE_PERF_MARK,
                apiStartMark
              );
              const measures = performance.getEntriesByName(
                FILTER_CHANGE_TO_API_CALL_MEASURE,
                'measure'
              );
              const last = measures[measures.length - 1];
              if (last) {
                console.info(
                  '[KPI] filter change to api call:',
                  last.duration.toFixed(2),
                  'ms'
                );
              }
              performance.clearMarks(FILTER_CHANGE_PERF_MARK);
              performance.clearMarks(apiStartMark);
              performance.clearMeasures(FILTER_CHANGE_TO_API_CALL_MEASURE);
            }
          }
        },
      },
    }
  );
}
